import { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useTran } from "@/hooks";
import { DecimalSeparator } from "@/lib/utils/number";
import {
  getUnitTypeMeta,
  isLevelAbove,
} from "@/features/organization/utils/organization.helpers";
import {
  buildTargetDataForFrequency,
  formatTargetDisplayValue,
  getMonthlyFillCount,
  isNumericTargetInputType,
  isMonthlyMode,
  MONTHS,
} from "../domains/targetSetup";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";

const INDENT_SIZE = 24;

const hasDisplayValue = (value) =>
  value !== undefined && value !== null && String(value) !== "";

const displayValue = (value) => (hasDisplayValue(value) ? String(value) : "-");

const formatOverviewTargetValue = (value, inputType) => {
  const display = formatTargetDisplayValue(value, inputType);
  if (display === "-" || !isNumericTargetInputType(inputType)) return display;

  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? DecimalSeparator(numericValue, 2)
    : display;
};

const MonthlyTargetReadOnlyGrid = ({
  months = {},
  monthOptions = [],
  inputType = "",
}) => (
  <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 xl:grid-cols-4">
    {(monthOptions.length > 0 ? monthOptions : MONTHS).map(({ key, label }) => {
      const value = formatOverviewTargetValue(months?.[key], inputType);

      return (
        <div key={key} className="flex min-w-0 items-center gap-1">
          <span className="w-12 shrink-0 text-[11px] font-medium leading-tight text-muted-foreground">
            {label}
          </span>
          <span
            title={value}
            className="flex h-7 min-w-0 flex-1 items-center rounded-md border border-input bg-background px-2 text-xs text-muted-foreground"
          >
            <span className="truncate">{value}</span>
          </span>
        </div>
      );
    })}
  </div>
);

const OrgTreeRowReadOnly = observer(
  ({
    node,
    monthOptions,
    targetFrequency,
    depth = 0,
    hasChildren,
    collapseKey,
    isCollapsed,
    onToggleCollapse,
  }) => {
    const { t } = useTran();
    const meta = getUnitTypeMeta(node.unitLevel);
    const Icon = meta.icon;
    const isMonthly = isMonthlyMode(targetFrequency);
    const inputType = node.inputType || "";
    const targetData = useMemo(
      () =>
        buildTargetDataForFrequency({
          targetData: node.targetData ?? {},
          disclosure: {
            inputType,
            defaultUnit: node.unit ?? node.targetData?.unit,
          },
          targetFrequency,
          monthOptions,
        }),
      [inputType, monthOptions, node.targetData, node.unit, targetFrequency],
    );
    const months = targetData.months ?? {};
    const targetValue = node.targetValue ?? targetData.value;
    const unit = node.unit ?? targetData.unit;
    const locationLabel =
      node.locationLabel || node.countryLabel || node.country;

    const handleToggle = (e) => {
      e.stopPropagation();
      onToggleCollapse?.(collapseKey);
    };

    return (
      <div
        className={
          isMonthly
            ? "flex flex-col gap-2 border-b border-border px-3 py-3"
            : "flex items-center gap-2 border-b border-border px-3 py-2.5"
        }
        style={{ paddingLeft: depth * INDENT_SIZE + 12 }}
      >
        <div className="flex w-full items-center gap-2">
          {hasChildren ? (
            <button
              type="button"
              onClick={handleToggle}
              className="flex h-5 w-5 items-center justify-center rounded border border-border bg-background text-muted-foreground hover:bg-muted"
            >
              {isCollapsed ? (
                <ChevronRight className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </button>
          ) : (
            <span className="ml-2 h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
          )}

          <span
            className={`flex h-7 w-7 items-center justify-center rounded-full bg-background shadow-sm ${meta.color}`}
          >
            <Icon className="h-3.5 w-3.5" />
          </span>

          <div className="flex flex-1 flex-col min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground truncate">
                {node.name}
              </span>
              {locationLabel ? (
                <Badge
                  variant="outline"
                  className="border-primary/30 text-[11px] text-primary shrink-0"
                >
                  {locationLabel}
                </Badge>
              ) : null}
            </div>
            <div className="flex flex-col gap-1">
              {node.code && !isLevelAbove(node.unitLevel, 2) ? (
                <div className="flex items-center gap-1.5 flex-nowrap">
                  <span className={`text-xs ${meta.color} shrink-0`}>
                    code: {t(node.code)}
                  </span>
                  <OrgStatusTag
                    status={node.status}
                    inactiveDate={node.inactiveDate}
                    className="text-[10px] shrink-0"
                  />
                </div>
              ) : (
                <OrgStatusTag
                  status={node.status}
                  inactiveDate={node.inactiveDate}
                  className="text-[10px] shrink-0"
                />
              )}
            </div>
          </div>

          {isMonthly ? (
            <span className="shrink-0 text-xs text-muted-foreground">
              {getMonthlyFillCount(months)}/12 {t("months")}
            </span>
          ) : (
            <div className="flex items-center gap-2 shrink-0">
              <span className="w-40 text-center text-sm text-muted-foreground">
                {formatOverviewTargetValue(targetValue, inputType)}
              </span>
              <span className="w-40 text-center text-sm text-muted-foreground">
                {displayValue(unit)}
              </span>
            </div>
          )}
        </div>

        {isMonthly ? (
          <div className="ml-9 w-[calc(100%-2.25rem)] space-y-1">
            <MonthlyTargetReadOnlyGrid
              months={months}
              monthOptions={monthOptions}
              inputType={inputType}
            />
            {hasDisplayValue(unit) ? (
              <p className="text-[11px] text-muted-foreground">
                {t("Unit")}: <span className="font-medium">{unit}</span>
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  },
);

OrgTreeRowReadOnly.displayName = "OrgTreeRowReadOnly";

export default OrgTreeRowReadOnly;
